/**
 * Issue API — /api/admin/issue/* (이슈 루프 UI, 2026-08-26)
 *
 *   fetchIssueList()      GET /api/admin/issue        — 목록(전체 + is_mine)
 *   fetchIssueDetail(id)  GET /api/admin/issue/:id    — 상세(요약·제보·이벤트)
 *
 * 필터(영역/심각도/상태/내 제보)는 클라이언트에서. is_mine 은 서버가 행마다 계산.
 */

import { getJson, patchJson, postJson } from './http';

export type IssueStatus = 'open' | 'in_progress' | 'resolved' | 'closed';

export interface IssueListRow {
  id:                 number;
  title:              string;
  category:           string | null;
  area:               string | null;
  domain:             string | null;
  subdomain:          string | null;
  tags:               string[];
  group_id:           number | null;
  group_size:         number;
  group_fresh:        boolean;
  due_date:           string | null;
  severity:           string | null;
  status:             IssueStatus;
  summary:            string | null;
  report_count:       number;
  reporter_count:     number;
  last_reported_at:   string | null;
  last_reporter_name: string | null;
  is_mine:            boolean;
  created_at:         string;
}

export interface IssueReport {
  id: number; reporter_name: string | null; slack_user_id: string | null;
  origin: string; kind: string; raw_text: string | null;
  extracted: Record<string, unknown> | null; created_at: string;
}
export interface IssueEvent {
  id: number; kind: string; from_status: string | null; to_status: string | null;
  actor: string; actor_name: string | null; message: string | null; created_at: string;
}
export interface CodeHint { file: string; line: number | null; match: string; }

export interface IssueDetail {
  id: number; title: string; summary: string | null;
  category: string | null; area: string | null;
  domain: string | null; subdomain: string | null; tags: string[];
  severity: string | null;
  status: IssueStatus; screen_key: string | null; created_at: string;
  purpose: string | null; goal: string | null; target: string | null; must_have: string | null;
  dev_note: string | null; code_hints: CodeHint[];
  group_id: number | null; group_fresh: boolean; due_date: string | null;
  group_members: Array<{ id: number; title: string; status: IssueStatus }>;
  reports: IssueReport[]; events: IssueEvent[];
}

/** GET /api/admin/issue — 이슈 목록 */
export async function fetchIssueList(
  opts: { signal?: AbortSignal } = {},
): Promise<{ rows: IssueListRow[] }> {
  return getJson<{ rows: IssueListRow[] }>('/api/admin/issue', { signal: opts.signal });
}

/** GET /api/admin/issue/:id — 이슈 상세(행 확장) */
export async function fetchIssueDetail(
  id: number | string,
  opts: { signal?: AbortSignal } = {},
): Promise<IssueDetail> {
  return getJson<IssueDetail>(`/api/admin/issue/${encodeURIComponent(String(id))}`, { signal: opts.signal });
}

export type IssueResolution = 'fixed' | 'rejected' | 'replanned' | 'guided';

export interface TransitionBody {
  to:          IssueStatus;
  resolution?: IssueResolution;
  note?:       string;
}

/** PATCH /api/admin/issue/:id/status — 상태 전이 */
export async function transitionIssue(
  id: number | string,
  body: TransitionBody,
): Promise<{ id: number; status: IssueStatus }> {
  return patchJson<{ id: number; status: IssueStatus }>(
    `/api/admin/issue/${encodeURIComponent(String(id))}/status`, body);
}

/** PATCH /api/admin/issue/:id/severity — 심각도 조정 */
export async function updateSeverity(
  id: number | string,
  severity: string,
): Promise<{ id: number; severity: string }> {
  return patchJson<{ id: number; severity: string }>(
    `/api/admin/issue/${encodeURIComponent(String(id))}/severity`, { severity });
}

/** PATCH /api/admin/issue/:id/due-date — 마감일 지정/해제 */
export async function updateDueDate(
  id: number | string,
  due_date: string | null,
): Promise<{ id: number; due_date: string | null }> {
  return patchJson<{ id: number; due_date: string | null }>(
    `/api/admin/issue/${encodeURIComponent(String(id))}/due-date`, { due_date });
}

/** POST /api/admin/issue/:id/enrich — 코드 기반 재분석(leader+). 결과 즉시 반환 */
export async function enrichIssue(
  id: number | string,
): Promise<{ dev_note: string | null; code_hints: CodeHint[] }> {
  return postJson<{ dev_note: string | null; code_hints: CodeHint[] }>(
    `/api/admin/issue/${encodeURIComponent(String(id))}/enrich`, {});
}
